import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Calendar, Receipt, Paperclip, Trash2, Eye,
  FileDown, TrendingUp, Upload, ExternalLink, ChevronDown, ChevronRight, Camera,
  Pencil,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { API } from "../../App";
import EditDeliveryLineModal from "./EditDeliveryLineModal";

const currentYearMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IE", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return iso.slice(0, 10); }
};

const TREATMENT_LABEL = {
  standard: "Std 23%",
  eu_reverse_charge: "EU RC",
  no_vat: "No VAT",
  unclassified: "Unclassified",
};

const TREATMENT_STYLE = {
  standard: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  eu_reverse_charge: "bg-purple-500/20 text-purple-300 border-purple-500/40",
  no_vat: "bg-slate-700 text-slate-300",
  unclassified: "bg-amber-500/20 text-amber-300 border-amber-500/40",
};

const MAX_FILE_MB = 10;
const ACCEPT_ATTR = "image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,application/pdf,.pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif";

/* ------------------------------------------------------------ */
/* Receipts modal — list + upload + delete + view for a row     */
/* ------------------------------------------------------------ */
const ReceiptsModal = ({ open, onClose, row, onCountChange }) => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const queryParams = useMemo(() => {
    if (!row) return null;
    return row.row_kind === "delivery"
      ? { delivery_id: row.delivery_id }
      : { unit_id: row.unit_id };
  }, [row]);

  const load = useCallback(async () => {
    if (!queryParams) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stock/purchase-receipts`, { params: queryParams });
      setReceipts(res.data.receipts || []);
      onCountChange?.(res.data.count || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load receipts");
    } finally { setLoading(false); }
  }, [queryParams, onCountChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !row) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`File too large. Maximum ${MAX_FILE_MB}MB.`);
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    if (row.row_kind === "delivery") form.append("delivery_id", row.delivery_id);
    else form.append("unit_id", row.unit_id);
    try {
      await axios.post(`${API}/stock/purchase-receipts/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleDelete = async (receipt_id) => {
    setDeletingId(receipt_id);
    try {
      await axios.delete(`${API}/stock/purchase-receipts/${receipt_id}`);
      toast.success("Receipt removed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally { setDeletingId(null); }
  };

  const viewReceipt = (r) => {
    // Open in new tab — backend Content-Disposition is inline
    const url = `${API}/stock/purchase-receipts/${r.receipt_id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl bg-slate-900 border-slate-700 text-white"
        data-testid="receipts-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="w-5 h-5 text-saturn-orange" />
            Supplier Receipts
          </DialogTitle>
          <p className="text-slate-400 text-xs mt-1">
            {row?.row_kind === "delivery"
              ? <>Delivery <span className="font-mono">{row?.delivery_id?.slice(-10)}</span> · {row?.vendor_name || "Unknown vendor"}</>
              : <>Unit <span className="font-mono">{row?.unit_id?.slice(-10)}</span> · {row?.vendor_name || "Unknown vendor"}</>}
          </p>
        </DialogHeader>

        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-saturn-orange animate-spin" />
            </div>
          ) : receipts.length === 0 ? (
            <div className="border border-slate-700 border-dashed rounded-lg p-8 text-center text-slate-400 text-sm">
              No receipts attached yet. Upload the supplier invoice or delivery note as image / PDF (max 10 MB).
            </div>
          ) : (
            <div className="border border-slate-700 rounded-lg overflow-hidden divide-y divide-slate-800">
              {receipts.map((r) => (
                <div key={r.receipt_id}
                     className="flex items-center gap-3 p-3 hover:bg-slate-800/50"
                     data-testid={`receipt-row-${r.receipt_id}`}>
                  <div className="flex-shrink-0">
                    {r.content_type === "application/pdf"
                      ? <FileDown className="w-5 h-5 text-rose-400" />
                      : <Receipt className="w-5 h-5 text-blue-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm truncate">{r.original_filename}</div>
                    <div className="text-slate-500 text-xs">
                      {formatDate(r.uploaded_at)} · {(r.size / 1024).toFixed(1)} KB
                      {r.uploaded_by && <> · {r.uploaded_by}</>}
                    </div>
                  </div>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => viewReceipt(r)}
                    className="h-8 text-slate-300 hover:text-white hover:bg-slate-700"
                    data-testid={`receipt-view-${r.receipt_id}`}
                    title="Open receipt"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => handleDelete(r.receipt_id)}
                    disabled={deletingId === r.receipt_id}
                    className="h-8 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                    data-testid={`receipt-delete-${r.receipt_id}`}
                    title="Remove receipt"
                  >
                    {deletingId === r.receipt_id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Trash2 className="w-4 h-4" />}
                  </Button>
                </div>
              ))}
            </div>
          )}

          <input
            ref={fileRef} type="file" accept={ACCEPT_ATTR}
            onChange={handleFileChange} className="hidden"
            data-testid="receipt-file-input"
          />
          {/* Dedicated camera input: on iOS Safari and Android Chrome this
              opens the rear camera directly. Desktop browsers fall back
              to a plain image picker — same UX as file picker. */}
          <input
            ref={cameraRef} type="file" accept="image/*" capture="environment"
            onChange={handleFileChange} className="hidden"
            data-testid="receipt-camera-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => cameraRef.current?.click()}
              disabled={uploading}
              variant="outline"
              className="border-saturn-orange/60 text-saturn-orange hover:bg-saturn-orange/10"
              data-testid="receipt-camera-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              Take photo
            </Button>
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="bg-saturn-orange hover:bg-saturn-orange/80 text-white"
              data-testid="receipt-upload-btn"
            >
              {uploading
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? "Uploading..." : "Attach file"}
            </Button>
          </div>
          <p className="text-slate-500 text-[11px]">
            Image or PDF · up to 10 MB
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  className="border-slate-600 text-slate-300"
                  data-testid="receipts-modal-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------------------------------------------ */
/* Purchases sub-tab                                             */
/* ------------------------------------------------------------ */
const PurchasesSubTab = () => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(currentYearMonth());
  const [downloading, setDownloading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [modalRow, setModalRow] = useState(null);
  // Line-level edit: staff opens this to correct data-entry mistakes
  // (bad cost, wrong roll size, etc.). `editingLine` carries the whole
  // line dict + a `_parent_row` reference so we can force-refresh just
  // that row after save/delete without a full network refetch.
  const [editingLine, setEditingLine] = useState(null);

  const monthOptions = useMemo(() => {
    const opts = [];
    const d = new Date();
    d.setDate(1);
    for (let i = 0; i < 24; i++) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const label = d.toLocaleDateString("en-IE", { month: "long", year: "numeric" });
      opts.push({ value: `${y}-${m}`, label });
      d.setMonth(d.getMonth() - 1);
    }
    return opts;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stock/admin/purchases`, { params: { month } });
      setRows(res.data.rows || []);
      setTotals(res.data.totals || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load purchases");
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const downloadPurchasesVatPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/stock/admin/purchases-vat-summary/pdf`,
        { params: { month }, responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `saturn-styling-purchases-vat-${month}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Purchases VAT PDF downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate purchases VAT PDF");
    } finally { setDownloading(false); }
  };

  const monthLabel = monthOptions.find(o => o.value === month)?.label || month;
  const toggleExpanded = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const bumpReceiptCount = (rowId, next) => {
    setRows(prev => prev.map(r =>
      r.row_id === rowId ? { ...r, attached_receipts_count: next } : r
    ));
  };

  return (
    <div className="space-y-6" data-testid="purchases-subtab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-white font-semibold text-lg tracking-tight">
            Purchases — {monthLabel}
          </h3>
          <p className="text-slate-400 text-xs">
            Monthly stock deliveries and one-off buys with attached supplier receipts
          </p>
        </div>
        <Button
          onClick={downloadPurchasesVatPdf}
          disabled={downloading}
          className="bg-saturn-orange hover:bg-saturn-orange/80 text-white"
          data-testid="purchases-download-pdf-btn"
        >
          {downloading
            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            : <FileDown className="w-4 h-4 mr-2" />}
          Purchases VAT PDF
        </Button>
      </div>

      {/* Totals cards */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4" data-testid="purchases-totals">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-lg p-4">
            <p className="text-slate-400 text-xs uppercase tracking-wider">Purchase rows</p>
            <p className="text-white text-2xl md:text-3xl font-bold mt-1">{rows.length}</p>
            <p className="text-slate-500 text-xs mt-1">{monthLabel}</p>
          </div>
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-lg p-4">
            <p className="text-slate-400 text-xs uppercase tracking-wider">Net spend</p>
            <p className="text-white text-2xl md:text-3xl font-bold mt-1">€{totals.net_total.toFixed(2)}</p>
            <p className="text-slate-500 text-xs mt-1">Ex-VAT</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/40 rounded-lg p-4">
            <p className="text-blue-300 text-xs uppercase tracking-wider">Input VAT (std 23%)</p>
            <p className="text-white text-2xl md:text-3xl font-bold mt-1"
               data-testid="purchases-vat-paid">
              €{totals.vat_paid_to_suppliers.toFixed(2)}
            </p>
            <p className="text-blue-300 text-xs mt-1">Paid to suppliers</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/40 rounded-lg p-4">
            <p className="text-emerald-300 text-xs uppercase tracking-wider">Gross paid</p>
            <p className="text-white text-2xl md:text-3xl font-bold mt-1"
               data-testid="purchases-gross-paid">
              €{totals.gross_total.toFixed(2)}
            </p>
            <p className="text-emerald-300 text-xs mt-1" title="Reverse-charge VAT excluded — never left the bank">
              Net + supplier VAT
            </p>
          </div>
          <div className="bg-gradient-to-br from-purple-500/20 to-fuchsia-500/20 border border-purple-500/40 rounded-lg p-4">
            <p className="text-purple-300 text-xs uppercase tracking-wider">EU reverse charge</p>
            <p className="text-white text-2xl md:text-3xl font-bold mt-1">
              €{totals.self_accounted_reverse_charge_vat.toFixed(2)}
            </p>
            <p className="text-purple-300 text-xs mt-1">Self-accounted</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-slate-300 text-xs mb-1 block flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Month
            </Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white"
                             data-testid="purchases-month-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[280px]">
                {monthOptions.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3 flex justify-end">
            <Button variant="outline" size="sm" onClick={load}
                    className="border-slate-600 text-slate-300"
                    data-testid="purchases-refresh-btn">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-900/50 border border-slate-700 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-saturn-orange animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16" data-testid="purchases-empty">
            <Receipt className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No purchases logged for {monthLabel}.</p>
            <p className="text-slate-500 text-xs mt-1">
              Purchases show up here as soon as you add stock or log a delivery with a cost.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="text-slate-400 w-8"></TableHead>
                <TableHead className="text-slate-400">Date</TableHead>
                <TableHead className="text-slate-400">Vendor</TableHead>
                <TableHead className="text-slate-400">Product(s)</TableHead>
                <TableHead className="text-slate-400 text-right">Net</TableHead>
                <TableHead className="text-slate-400 text-right">VAT</TableHead>
                <TableHead className="text-slate-400 text-right">Gross</TableHead>
                <TableHead className="text-slate-400">VAT Treatment</TableHead>
                <TableHead className="text-slate-400 text-right">Receipts</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const isOpen = !!expanded[row.row_id];
                const productNames = row.lines.map(l => l.product_name).filter(Boolean);
                const productLabel = productNames.length <= 1
                  ? (productNames[0] || "—")
                  : `${productNames[0]} +${productNames.length - 1} more`;
                return (
                  <React.Fragment key={row.row_id}>
                    <TableRow
                      className="border-slate-800 hover:bg-slate-800/50 cursor-pointer"
                      onClick={() => toggleExpanded(row.row_id)}
                      data-testid={`purchases-row-${row.row_id}`}
                    >
                      <TableCell>
                        {isOpen
                          ? <ChevronDown className="w-4 h-4 text-slate-400" />
                          : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm whitespace-nowrap">
                        {formatDate(row.purchase_date)}
                      </TableCell>
                      <TableCell className="text-white text-sm">
                        {row.vendor_name || <span className="text-slate-500">—</span>}
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">
                        <div className="flex items-center gap-2">
                          <span>{productLabel}</span>
                          {row.row_kind === "delivery" && (
                            <Badge className="bg-slate-700 text-slate-300 text-xs">
                              {row.line_count} lines
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300 text-right">€{row.net_total.toFixed(2)}</TableCell>
                      <TableCell className="text-amber-400 text-right">€{row.vat_total.toFixed(2)}</TableCell>
                      <TableCell className="text-white font-bold text-right">€{row.gross_total.toFixed(2)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {row.vat_treatments.map(t => (
                            <Badge key={t} className={`text-xs ${TREATMENT_STYLE[t] || TREATMENT_STYLE.no_vat}`}>
                              {TREATMENT_LABEL[t] || t}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm" variant="ghost"
                          onClick={(e) => { e.stopPropagation(); setModalRow(row); }}
                          className={`h-8 gap-1 ${row.attached_receipts_count > 0
                            ? "text-saturn-orange hover:text-orange-300 hover:bg-orange-500/10"
                            : "text-slate-400 hover:text-white hover:bg-slate-700"}`}
                          data-testid={`purchases-receipts-btn-${row.row_id}`}
                          title="Attach or view receipts"
                        >
                          <Paperclip className="w-4 h-4" />
                          <span className="text-xs font-semibold">{row.attached_receipts_count}</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow className="border-slate-800 bg-slate-900/70">
                        <TableCell></TableCell>
                        <TableCell colSpan={8} className="py-2">
                          <div className="text-xs text-slate-400 mb-2 uppercase tracking-wider">
                            {row.row_kind === "delivery" ? "Delivery lines" : "Unit"}
                          </div>
                          <div className="border border-slate-800 rounded overflow-hidden">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-slate-800/50 text-slate-400 text-xs">
                                  <th className="text-left px-3 py-2">Product</th>
                                  <th className="text-right px-3 py-2">Qty</th>
                                  <th className="text-right px-3 py-2">Net</th>
                                  <th className="text-right px-3 py-2">VAT</th>
                                  <th className="text-left px-3 py-2">Treatment</th>
                                  <th className="text-right px-3 py-2 w-16"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {row.lines.map((l, idx) => (
                                  <tr key={l.unit_id || idx} className="border-t border-slate-800">
                                    <td className="text-white px-3 py-1.5">{l.product_name || "—"}</td>
                                    <td className="text-slate-300 text-right px-3 py-1.5">
                                      {l.quantity_count || 1}
                                    </td>
                                    <td className="text-slate-300 text-right px-3 py-1.5">
                                      €{(l.cost || 0).toFixed(2)}
                                    </td>
                                    <td className="text-amber-400 text-right px-3 py-1.5">
                                      €{((l.vat_amount || 0) + (l.reverse_charge_vat || 0)).toFixed(2)}
                                    </td>
                                    <td className="text-slate-300 px-3 py-1.5">
                                      <Badge className={`text-xs ${TREATMENT_STYLE[l.vat_treatment || "unclassified"]}`}>
                                        {TREATMENT_LABEL[l.vat_treatment || "unclassified"]}
                                      </Badge>
                                    </td>
                                    <td className="text-right px-2 py-1.5">
                                      {/* Edit line — opens the correction modal.
                                          Admin-gated on the backend (PATCH /units); UI shows
                                          the button unconditionally to avoid stale role checks. */}
                                      {l.unit_id && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingLine({ ...l, _parent_row_id: row.row_id });
                                          }}
                                          className="h-7 w-7 p-0 text-slate-400 hover:text-saturn-orange hover:bg-slate-800"
                                          title="Edit this line (fix cost, VAT, vendor)"
                                          data-testid={`purchases-edit-line-${l.unit_id}`}
                                        >
                                          <Pencil className="w-3.5 h-3.5" />
                                        </Button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {rows.length > 0 && totals && (
        <div className="flex items-center justify-between text-slate-400 text-sm bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3">
          <span className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-saturn-orange" />
            <strong className="text-white">{rows.length}</strong> purchase row(s) ·{" "}
            <strong className="text-white">{monthLabel}</strong>
          </span>
          <span className="text-xs">
            Gross paid €{totals.gross_total.toFixed(2)} · Reclaim €{totals.vat_paid_to_suppliers.toFixed(2)} input VAT · Declare €{totals.self_accounted_reverse_charge_vat.toFixed(2)} self-accounted on your VAT3.
          </span>
        </div>
      )}

      <ReceiptsModal
        open={!!modalRow}
        onClose={() => setModalRow(null)}
        row={modalRow}
        onCountChange={(n) => modalRow && bumpReceiptCount(modalRow.row_id, n)}
      />

      <EditDeliveryLineModal
        open={!!editingLine}
        onClose={() => setEditingLine(null)}
        line={editingLine}
        onSaved={load}
        onDeleted={load}
      />
    </div>
  );
};

export default PurchasesSubTab;
